import { bffRequestHeaders } from './bffFetch';
import { WorkspaceFeatureIds } from '../constants/workspaceFeatureIds';
import type {
  WorkspaceSubscription,
  WorkspaceSubscriptionCustomerContext,
} from './workspaceSubscriptionTypes';

// Feature IDs that gate any meaningful access to Clarity. A workspace
// subscription with none of these active provides no usable surface, so we
// treat that as equivalent to an unusable SKU.
const CLARITY_FEATURE_IDS: readonly number[] = [
  WorkspaceFeatureIds.ClarityOutOfBoxReports,
  WorkspaceFeatureIds.ClarityCustomReports,
];

export function parseWorkspaceSubscriptionFromBff(
  raw: Record<string, unknown>,
): WorkspaceSubscription | null {
  const wsId = Number(raw.workspaceId ?? raw.WorkspaceId ?? 0);
  if (!wsId)
    return null;
  const featsRaw = (raw.features ?? raw.Features) as unknown;
  const featureList = Array.isArray(featsRaw)
    ? featsRaw.map((fr) => {
        const r = fr as Record<string, unknown>;
        return {
          id: Number(r.id ?? r.Id ?? 0),
          name: (r.name ?? r.Name) as string | null,
          featureType: Number(r.featureType ?? r.FeatureType ?? 0),
          isActive: Boolean(r.isActive ?? r.IsActive ?? false),
        };
      })
    : [];
  const skuRaw = (raw.skuDetails ?? raw.SkuDetails) as Record<string, unknown> | null | undefined;
  const skuDetails = skuRaw
    ? {
        skuId: Number(skuRaw.skuId ?? skuRaw.SkuId ?? 0),
        isActive: Boolean(skuRaw.isActive ?? skuRaw.IsActive ?? false),
        isTrial: Boolean(skuRaw.isTrial ?? skuRaw.IsTrial ?? false),
        deploymentType: Number(skuRaw.deploymentType ?? skuRaw.DeploymentType ?? 0),
        startDate: String(skuRaw.startDate ?? skuRaw.StartDate ?? ''),
        expirationDate: String(skuRaw.expirationDate ?? skuRaw.ExpirationDate ?? ''),
      }
    : null;
  return {
    workspaceId: wsId,
    features: featureList,
    skuDetails,
  };
}

function readOptionalNumberFromRecord(
  data: Record<string, unknown>,
  pascalKey: string,
): number | null {
  const v = data[pascalKey];
  if (v === null || v === undefined)
    return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function readWorkspaceSubscriptionCustomerContext(
  raw: Record<string, unknown>,
): WorkspaceSubscriptionCustomerContext {
  const wsRaw = raw.WorkspaceSubscription ?? raw.workspaceSubscription;
  const workspaceSubscription
    = wsRaw && typeof wsRaw === 'object'
      ? parseWorkspaceSubscriptionFromBff(wsRaw as Record<string, unknown>)
      : null;
  return {
    selectedWorkspaceId: readOptionalNumberFromRecord(raw, 'SelectedWorkspaceId'),
    workspaceRoleId: readOptionalNumberFromRecord(raw, 'SelectedWorkspaceRoleId') ?? 0,
    workspaceSubscription,
    isWorkspaceSuperAdmin: Boolean(raw.IsWorkspaceSuperAdmin ?? raw.isWorkspaceSuperAdmin ?? false),
  };
}

/** Reads SelectedWorkspace*, WorkspaceSubscription, and IsWorkspaceSuperAdmin from a BFF JSON object. */
export function parseWorkspaceContextFromBffMePayload(
  raw: Record<string, unknown>,
): WorkspaceSubscriptionCustomerContext {
  return readWorkspaceSubscriptionCustomerContext(raw);
}

export async function fetchWorkspaceSubscriptionForCustomer(
  customerId: number,
  signal?: AbortSignal,
): Promise<WorkspaceSubscriptionCustomerContext> {
  if (customerId <= 0) {
    return {
      selectedWorkspaceId: null,
      workspaceRoleId: 0,
      workspaceSubscription: null,
      isWorkspaceSuperAdmin: false,
    };
  }
  const response = await fetch('/bff/api/me/workspace-subscription', {
    method: 'GET',
    credentials: 'include',
    headers: bffRequestHeaders(customerId),
    ...(signal ? { signal } : {}),
  });
  if (!response.ok) {
    return {
      selectedWorkspaceId: null,
      workspaceRoleId: 0,
      workspaceSubscription: null,
      isWorkspaceSuperAdmin: false,
    };
  }
  const raw = (await response.json()) as Record<string, unknown>;
  return readWorkspaceSubscriptionCustomerContext(raw);
}

export function isWorkspaceSkuUsable(sub: WorkspaceSubscription | null, bypassWorkspaceGuards = false): boolean {
  return classifyWorkspaceSkuState(sub, bypassWorkspaceGuards) === 'usable';
}

/**
 * Narrows the reason a workspace SKU is not usable so the UI can show
 * the right copy on the full-screen block screen rendered by SignUpFlow.
 *
 * - `usable`: super-admin OR active SKU not past its expiration date with
 *    at least one Clarity feature active.
 * - `noSku`: Basecamp returned no subscription / SKU for the workspace.
 * - `disabled`: SKU exists but `IsActive` is false. In Basecamp this is a
 *    manual admin flag on the WorkspaceSku join row (soft-disable). It
 *    does NOT track expiration on its own.
 * - `trialExpired` / `subscriptionExpired`: SKU is active but the stored
 *    `ExpirationDate` has passed. We compute this in the SPA because
 *    Basecamp does not flip `IsActive` automatically on expiry.
 * - `noFeatures`: SKU is valid but no Clarity-gating features are active,
 *    so the app has nothing to render. Treated as a block to avoid the
 *    redirect loop between `/` and the per-route feature gates.
 */
export type WorkspaceSkuState =
  | 'usable'
  | 'noSku'
  | 'disabled'
  | 'trialExpired'
  | 'subscriptionExpired'
  | 'noFeatures';

export function classifyWorkspaceSkuState(
  sub: WorkspaceSubscription | null,
  bypassWorkspaceGuards = false,
): WorkspaceSkuState {
  if (bypassWorkspaceGuards)
    return 'usable';

  const sku = sub?.skuDetails;
  if (!sku)
    return 'noSku';

  if (!sku.isActive)
    return 'disabled';

  const exp = new Date(sku.expirationDate);
  if (Number.isNaN(exp.getTime()) || exp <= new Date())
    return sku.isTrial ? 'trialExpired' : 'subscriptionExpired';

  const hasAnyClarityFeature = sub!.features.some(
    (f) => f.isActive && CLARITY_FEATURE_IDS.includes(f.id),
  );
  if (!hasAnyClarityFeature)
    return 'noFeatures';

  return 'usable';
}

export function hasActiveWorkspaceFeature(
  sub: WorkspaceSubscription | null,
  featureId: number,
  bypassWorkspaceGuards = false,
): boolean {
  if (bypassWorkspaceGuards)
    return true;
  if (!isWorkspaceSkuUsable(sub, false))
    return false;
  return sub!.features.some((f) => f.id === featureId && f.isActive);
}

export function hasAnyActiveWorkspaceFeature(
  sub: WorkspaceSubscription | null,
  featureIds: readonly number[],
  bypassWorkspaceGuards = false,
): boolean {
  if (bypassWorkspaceGuards)
    return true;
  return featureIds.some((id) => hasActiveWorkspaceFeature(sub, id, false));
}

export async function fetchWorkspaceSubscription(
  workspaceId: number,
  currentCustomerId: number,
  signal?: AbortSignal,
): Promise<WorkspaceSubscription | null> {
  const response = await fetch(`/bff/api/workspaceSubscription/${workspaceId}`, {
    method: 'GET',
    credentials: 'include',
    headers: bffRequestHeaders(currentCustomerId),
    ...(signal ? { signal } : {}),
  });
  if (!response.ok)
    return null;
  const raw = (await response.json()) as Record<string, unknown>;
  return parseWorkspaceSubscriptionFromBff(raw);
}
